import React, { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import Icon from './Icon'

/**
 * Enterprise right-click context menu.
 *
 * Props:
 *   open    {boolean}
 *   x       {number}   — clientX from the context-menu event
 *   y       {number}   — clientY from the context-menu event
 *   onClose {function}
 *   header  {{ label: string, value: string }} — optional header row
 *   items   {Array<{ id, label, icon, onSelect, kind?, disabled? }>}
 *            kind: 'default' | 'danger'
 */
export default function ContextMenu({ open, x = 0, y = 0, onClose, header, items = [] }) {
  const { t } = useTranslation()
  const menuRef = useRef(null)
  const focusIdxRef = useRef(-1)

  const close = useCallback(() => {
    focusIdxRef.current = -1
    onClose?.()
  }, [onClose])

  // Close on click-outside, scroll, ESC
  useEffect(() => {
    if (!open) return
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) close()
    }
    const handleKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close() }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const btns = menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])')
        if (!btns?.length) return
        focusIdxRef.current = Math.min(focusIdxRef.current + 1, btns.length - 1)
        btns[focusIdxRef.current]?.focus()
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        const btns = menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])')
        if (!btns?.length) return
        focusIdxRef.current = Math.max(focusIdxRef.current - 1, 0)
        btns[focusIdxRef.current]?.focus()
      }
    }
    window.addEventListener('mousedown', handleClick)
    window.addEventListener('scroll', close, true)
    window.addEventListener('keydown', handleKey)
    return () => {
      window.removeEventListener('mousedown', handleClick)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('keydown', handleKey)
    }
  }, [open, close])

  // Viewport-edge clamping — runs after the menu mounts and measures itself
  const positionRef = useRef({ top: y, left: x })
  useEffect(() => {
    if (!open || !menuRef.current) return
    const rect = menuRef.current.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    let top = y
    let left = x
    if (left + rect.width > vw - 8) left = vw - rect.width - 8
    if (top + rect.height > vh - 8) top = vh - rect.height - 8
    if (left < 8) left = 8
    if (top < 8) top = 8
    positionRef.current = { top, left }
    menuRef.current.style.top = `${top}px`
    menuRef.current.style.left = `${left}px`
  }, [open, x, y])

  if (!open) return null

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={t('common.a11y.contextMenu')}
      onContextMenu={e => e.preventDefault()}
      className="dropdown-menu show"
      style={{
        position: 'fixed',
        top: y,
        left: x,
        zIndex: 'var(--z-dropdown)',
        minWidth: 230,
        padding: 4,
        animation: 'fadeInScale 110ms var(--ease-out) both',
        userSelect: 'none',
        outline: 'none',
      }}
    >
      {header && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '7px 10px 6px',
          borderBottom: '1px solid var(--border-subtle)',
          marginBottom: 2,
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-2xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'var(--text-muted)',
            fontWeight: 'var(--fw-semi)',
            flexShrink: 0,
          }}>
            {header.label}
          </span>
          {header.value && (
            <span style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-2xs)',
              color: 'var(--text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 140,
              direction: 'rtl',
              textAlign: 'right',
            }} title={header.value}>
              {header.value}
            </span>
          )}
        </div>
      )}

      {items.map((item) => (
        <ContextMenuItem key={item.id} item={item} onClose={close} />
      ))}
    </div>,
    document.body
  )
}

function ContextMenuItem({ item, onClose }) {
  const { label, icon, onSelect, kind = 'default', disabled = false } = item
  const isDanger = kind === 'danger'

  const handleClick = () => {
    if (disabled) return
    onClose()
    onSelect?.()
  }

  return (
    <button
      role="menuitem"
      disabled={disabled}
      onClick={handleClick}
      className={`dropdown-item d-flex align-items-center gap-2${isDanger ? ' text-danger' : ''}`}
      style={{
        borderRadius: 'var(--radius-xs)',
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-med)',
      }}
    >
      {icon && (
        <Icon
          name={icon}
          size={14}
          color={disabled ? 'var(--text-muted)' : isDanger ? 'var(--danger)' : 'var(--text-secondary)'}
          strokeWidth={1.8}
          style={{ flexShrink: 0 }}
        />
      )}
      {label}
    </button>
  )
}
